import * as React from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Button, Col, Form, Popover, Checkbox, Row } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { withUserType } from '../withUserType';
import { CopyOutlined } from '@ant-design/icons';
import { TimeRangePickerWith24Hours } from '../operating_hours_card_display/TimePicker24Hour';

const DAYS_OF_THE_WEEK = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

const LocationFormHoursComponent: React.FC<any> = ({ form, location = {} }) => {
	const operatingHours = useMemo(
		() => nullSafeGetOrElse('operatingHours', location, {}),
		[location]
	);
	const { getFieldDecorator } = form;
	const [openKeys, setOpenKeys] = useState(Object.keys(operatingHours));

	useEffect(() => {
		const initialValues = DAYS_OF_THE_WEEK.reduce((acc, day) => {
			const from = nullSafeGet(`${day}.from`, operatingHours)
				? moment(nullSafeGet(`${day}.from`, operatingHours), 'h:mm a')
				: undefined;
			const to = nullSafeGet(`${day}.to`, operatingHours)
				? moment(nullSafeGet(`${day}.to`, operatingHours), 'h:mm a')
				: undefined;
			acc[day] = { from, to };
			return acc;
		}, {});

		form.setFieldsValue(initialValues);
		setOpenKeys(Object.keys(operatingHours)); // also reset which days are open
	}, [operatingHours]);

	const openDay = (dayOfWeek) => {
		setOpenKeys((prev) => (prev.includes(dayOfWeek) ? prev : [...prev, dayOfWeek]));
	};

	const closeDay = (dayOfWeek) => {
		setOpenKeys((prev) => prev.filter((key) => key !== dayOfWeek));
	};

	useEffect(() => {
		form.setFieldsValue({ openKeys: openKeys });
	}, [openKeys]);

	const CopyToDayContent = ({ today }) => {
		const [copyKeys, setCopyKeys] = useState([]);

		const handleCopyToSelected = (sourceDay) => {
			const src = form.getFieldValue(sourceDay);
			if (!nullSafeGet('from', src) || !nullSafeGet('to', src)) return;

			copyKeys.forEach((day) => {
				openDay(day);
				form.setFieldsValue({
					[day]: { from: src.from, to: src.to },
				});
			});

			setCopyKeys([]);
		};

		return (
			<div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
				<div style={{ marginBottom: 8, fontWeight: 500 }}>Copy {today}’s hours to:</div>
				<Checkbox.Group
					value={copyKeys}
					onChange={(vals) => setCopyKeys(vals)}
					style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}
				>
					{DAYS_OF_THE_WEEK.filter((d) => d !== today).map((d) => (
						<Checkbox key={d} value={d} style={{ width: '100%', margin: 0 }}>
							{d}
						</Checkbox>
					))}
				</Checkbox.Group>
				<Button
					type="primary"
					size="small"
					block
					disabled={copyKeys.length === 0}
					style={{ marginTop: 8 }}
					onClick={() => handleCopyToSelected(today)}
				>
					Copy
				</Button>
			</div>
		);
	};

	return DAYS_OF_THE_WEEK.map((dayOfWeek, idx) => {
		const from = nullSafeGet(`${dayOfWeek}.from`, operatingHours)
			? moment(nullSafeGet(`${dayOfWeek}.from`, operatingHours), 'h:mm a')
			: undefined;
		const to = nullSafeGet(`${dayOfWeek}.to`, operatingHours)
			? moment(nullSafeGet(`${dayOfWeek}.to`, operatingHours), 'h:mm a')
			: undefined;

		const isActive = openKeys.includes(dayOfWeek);

		return (
			<Row style={{ alignItems: 'center', minHeight: 40, marginBottom: 8, gap: 8 }}>
				<Col flex="none">
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
						<span style={{ minWidth: 75 }}>{dayOfWeek}</span>
						<span style={{ color: isActive ? 'green' : 'gray', minWidth: 45 }}>
							{isActive ? 'Open' : 'Closed'}
						</span>
					</div>
				</Col>
				{getFieldDecorator(`openKeys`, {
					initialValue: openKeys,
				})(<></>)}
				<Col flex="auto">
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: 4,
						}}
					>
						<Form.Item
							hidden={!isActive}
							style={{
								marginBottom: 0,
								maxWidth: 220,
								flex: '0 1 auto',
							}}
						>
							{getFieldDecorator(`${dayOfWeek}`, {
								initialValue: { from, to },
								rules: [
									{
										validator: async (_, value) => {
											// only validate if the day is open
											if (isActive) {
												if (!value.from || !value.to) {
													throw new Error(`Both start and end times required.`);
												}
											}
										},
									},
								],
							})(
								<TimeRangePickerWith24Hours
									use12Hours
									name={dayOfWeek}
									format="h:mm a"
									minuteStep={15}
									showNow={false}
								/>
							)}
						</Form.Item>

						<div style={{ display: 'flex', gap: 2 }}>
							{isActive ? (
								<>
									<Button className="rowButton" type="text" onClick={() => closeDay(dayOfWeek)}>
										<i className="icons8-font icons8-trash-can" />
									</Button>
									<Popover placement="bottomRight" content={<CopyToDayContent today={dayOfWeek} />}>
										<Button className="rowButton" type="text">
											<CopyOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
										</Button>
									</Popover>
								</>
							) : (
								<Button className="rowButton" type="text" onClick={() => openDay(dayOfWeek)}>
									<i className="icons8-font icons8-plus" />
								</Button>
							)}
						</div>
					</div>
				</Col>
			</Row>
		);
	});
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(LocationFormHoursComponent)
);
